// API service for communicating with the backend
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.astarx.cn";

// Token management
const TOKEN_STORAGE_KEY = "refresh_token";
const ACCESS_TOKEN_KEY = "access_token";

// Store tokens in localStorage
export const tokenManager = {
  getRefreshToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
  },

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
  },
};

let refreshPromise: Promise<boolean> | null = null;

// Types for API responses
export interface ApiUser {
  user_id: string;
  username: string;
  email: string;
  phone: string;
  group_id: string | null;
  created_at: string;
  updated_at: string;
  group?: {
    group_id: string;
    group_name: string;
    user_ids: string[];
    created_by: string;
    created_at: string;
    updated_at: string;
  };
}

export interface ApiTransaction {
  transaction_id: string;
  date: string;
  description: string;
  category: string;
  amount: number;
  payer_id: string;
  group_id: string;
  created_at: string;
  updated_at: string;
  payer_username: string;
}

export interface ApiIncome {
  income_id: string;
  date: string;
  description: string;
  category: string;
  amount: number;
  receiver_id: string;
  group_id: string;
  created_at: string;
  updated_at: string;
  receiver_username?: string;
}

export interface ApiIncomeListResponse {
  incomes: ApiIncome[];
  total_income: number;
  month: string;
  group_id: string;
}

export interface ApiGroupBudget {
  id: number;
  group_id: string;
  category_group: string;
  category: string;
  category_budget: string | number;
  created_at: string;
  updated_at: string;
}


export interface ApiCategoryGroupBudget {
  category_group: string;
  subtotal: string | number;
  categories: ApiGroupBudget[];
}

export interface ApiGroupBudgetSummary {
  group_id: string;
  category_groups: ApiCategoryGroupBudget[];
  total_budget: string | number;
  created_at: string;
  updated_at: string;
}

export interface ApiBudgetListResponse {
  budgets: ApiGroupBudget[];
  count: number;
  message?: string;
}

export interface ApiMonthlySummary {
  summary_id: string;
  group_id: string;
  month_year: string;
  total_income: number;
  total_expenses: number;
  net_balance: number;
  transaction_count: number;
  category_breakdown: { [key: string]: number };
  payer_breakdown: { [key: string]: number };
  created_at: string;
  updated_at: string;
}

export interface ApiMonthlyData {
  transactions: ApiTransaction[];
  summary: ApiMonthlySummary;
  month: string;
  group_id: string;
  pagination?: {
    page: number;
    limit: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface ApiAssetCategory {
  category_id: string;
  group_id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ApiAssetItem {
  asset_item_id: string;
  group_id: string;
  category_id: string;
  name: string;
  is_active: boolean;
  amount: string;
  snapshot_date: string;
  created_at: string;
  updated_at: string;
  category_name?: string;
}

export interface ApiAssetAllocation {
  category_id: string;
  category_name: string;
  total_amount: string;
  percentage: string;
}

export interface ApiAssetListResponse {
  assets: ApiAssetItem[];
  total_amount: string;
  allocation: ApiAssetAllocation[];
  group_id: string;
}

export interface ApiMonthlyAssetReview {
  month: string;
  total_amount: string;
  allocation: ApiAssetAllocation[];
}

export interface ApiMonthlyAssetReviewResponse {
  group_id: string;
  months: ApiMonthlyAssetReview[];
}

export interface ApiTransferRequest {
  from_asset_item_id: string;
  to_asset_item_id: string;
  amount: string;
  snapshot_date: string;
}

// Refresh session function
const refreshAuthToken = async (): Promise<boolean> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const activeRefreshPromise = (async () => {
    try {
      const refreshToken = tokenManager.getRefreshToken();
      const refreshPayload: any = {
        session_id: "session",
        refresh_both_tokens: true,
      };

      if (refreshToken) {
        refreshPayload.refresh_token = refreshToken;
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(refreshPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Refresh failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();

      if (data.access_token) {
        tokenManager.setAccessToken(data.access_token);
      }
      if (data.refresh_token) {
        tokenManager.setRefreshToken(data.refresh_token);
      }

      return true;
    } catch (error) {
      console.error("Session refresh failed:", error);
      tokenManager.clearTokens();
      return false;
    }
  })();

  refreshPromise = activeRefreshPromise;
  try {
    return await activeRefreshPromise;
  } finally {
    if (refreshPromise === activeRefreshPromise) {
      refreshPromise = null;
    }
  }
};

// Income API
export const incomeApi = {
  async createIncome(incomeData: {
    date: string;
    description: string;
    category: string;
    amount: number;
    receiver_id: string;
    group_id: string;
  }): Promise<ApiIncome> {
    const response = await fetchWithAuth(`${API_BASE_URL}/incomes`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(incomeData),
    });
    return handleResponse(response);
  },

  async getMonthlyIncomes(
    groupId: string,
    month?: string
  ): Promise<ApiIncomeListResponse> {
    const params = new URLSearchParams();
    if (month) params.append("month", month);

    const url = `${API_BASE_URL}/groups/${groupId}/incomes${
      params.toString() ? "?" + params.toString() : ""
    }`;

    const response = await fetchWithAuth(url);
    return handleResponse(response);
  },

  async updateIncome(
    incomeId: string,
    updates: Partial<{
      date: string;
      description: string;
      category: string;
      amount: number;
      receiver_id: string;
      group_id: string;
    }>
  ): Promise<ApiIncome> {
    const response = await fetchWithAuth(`${API_BASE_URL}/incomes/${incomeId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  async deleteIncome(incomeId: string) {
    const response = await fetchWithAuth(`${API_BASE_URL}/incomes/${incomeId}`, {
      method: "DELETE",
    });

    if (response.status === 204) {
      return null;
    }
    return handleResponse(response);
  },
};

// Group Budget API
export const budgetApi = {
  async getGroupBudgets(groupId: string): Promise<ApiGroupBudgetSummary> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/budgets`);
    return handleResponse(response);
  },

  async createBudget(budgetData: {
    group_id: string;
    category_group: string;
    category: string;
    category_budget: number;
  }): Promise<ApiGroupBudget> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${budgetData.group_id}/budgets`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(budgetData),
    });
    return handleResponse(response);
  },

  async updateBudget(
    groupId: string,
    budgetId: number,
    updates: Partial<{
      category_group: string;
      category: string;
      category_budget: number;
    }>
  ): Promise<ApiGroupBudget> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/budgets/${budgetId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  async deleteBudget(groupId: string, budgetId: number): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/budgets/${budgetId}`, {
      method: "DELETE",
    });
    if (response.status === 204) {
      return;
    }
    return handleResponse(response);
  },

  async renewBudgets(
    groupId: string,
    budgets: Array<{
      category_group: string;
      category: string;
      category_budget: number;
    }>
  ): Promise<ApiBudgetListResponse> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/budgets/renew`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ budgets }),
    });
    return handleResponse(response);
  },
};

// Asset API
export const assetApi = {
  async getCategories(groupId: string): Promise<ApiAssetCategory[]> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/categories`);
    return handleResponse(response);
  },

  async createCategory(groupId: string, name: string): Promise<ApiAssetCategory> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/categories`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });
    return handleResponse(response);
  },

  async updateCategory(groupId: string, categoryId: string, name: string): Promise<ApiAssetCategory> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/categories/${categoryId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });
    return handleResponse(response);
  },

  async deleteCategory(groupId: string, categoryId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/categories/${categoryId}`, {
      method: "DELETE",
    });
    if (response.status === 204) {
      return;
    }
    return handleResponse(response);
  },

  async getAssets(groupId: string, month?: string): Promise<ApiAssetListResponse> {
    const params = new URLSearchParams();
    if (month) params.append("month", month);
    const response = await fetchWithAuth(
      `${API_BASE_URL}/groups/${groupId}/assets${params.toString() ? "?" + params.toString() : ""}`
    );
    return handleResponse(response);
  },

  async createAsset(
    groupId: string,
    assetData: {
      category_id: string;
      name: string;
      amount: string;
      snapshot_date: string;
    }
  ): Promise<ApiAssetItem> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(assetData),
    });
    return handleResponse(response);
  },

  async updateAsset(
    groupId: string,
    assetId: string,
    updates: Partial<{
      category_id: string;
      name: string;
      amount: string;
      snapshot_date: string;
    }>
  ): Promise<ApiAssetItem> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/${assetId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    });
    return handleResponse(response);
  },

  async deleteAsset(groupId: string, assetId: string): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/${assetId}`, {
      method: "DELETE",
    });
    if (response.status === 204) {
      return;
    }
    return handleResponse(response);
  },

  async getMonthlyReview(groupId: string, from: string, to: string): Promise<ApiMonthlyAssetReviewResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/groups/${groupId}/assets/monthly-review?from=${from}&to=${to}`
    );
    return handleResponse(response);
  },

  async transfer(groupId: string, data: ApiTransferRequest): Promise<void> {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}/assets/transfer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (response.status === 204) {
      return;
    }
    return handleResponse(response);
  },
};

// Enhanced fetch function with automatic token refresh
const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // For this API, we primarily rely on session cookies, not Bearer tokens
  const headers = {
    ...options.headers,
  };

  // Make the initial request (relying on session cookies)
  let response = await fetch(url, {
    ...options,
    headers,
    credentials: "include", // Always include cookies for session-based auth
  });

  // If we get a 401, try to refresh the session and retry
  if (response.status === 401) {
    const refreshSuccess = await refreshAuthToken();

    if (refreshSuccess) {
      // Retry the original request (session should be refreshed via cookies)
      response = await fetch(url, {
        ...options,
        headers,
        credentials: "include",
      });
    } else {
      console.error("❌ Refresh failed, dispatching logout event");
      // Refresh failed, dispatch logout event
      window.dispatchEvent(new CustomEvent("auth:logout"));
    }
  }

  return response;
};

// Helper function to handle API responses
const handleResponse = async (response: Response) => {
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Network error" }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }
  return response.json();
};

// Authentication API
export const authApi = {
  async login(usernameOrEmail: string, password: string) {
    // Use regular fetch for login to avoid infinite recursion
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        username_or_email: usernameOrEmail,
        password: password,
      }),
    });

    const data = await handleResponse(response);

    // Store access token from login
    if (data?.session?.access_token) {
      tokenManager.setAccessToken(data.session.access_token);
    }
    // Store refresh token from login
    if (data?.session?.refresh_token) {
      tokenManager.setRefreshToken(data.session.refresh_token);
    }

    return data;
  },

  async logout() {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
    });

    // Clear tokens on logout
    tokenManager.clearTokens();

    return handleResponse(response);
  },

  async getCurrentUser(): Promise<{ user: ApiUser; session: any }> {
    // Use regular fetch for getCurrentUser to avoid issues during initial auth check
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/me`, {
      credentials: "include",
    });

    const data = await handleResponse(response);

    // Store tokens if returned (e.g., from session-based auth)
    if (data.access_token) {
      tokenManager.setAccessToken(data.access_token);
    }
    if (data.refresh_token) {
      tokenManager.setRefreshToken(data.refresh_token);
    }


    return data;
  },

  async register(userData: {
    username: string;
    email: string;
    phone: string;
    password: string;
  }) {
    const response = await fetchWithAuth(`${API_BASE_URL}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  async refreshToken() {
    return refreshAuthToken();
  },
};

// Group API
export const groupApi = {
  async createGroup(groupName: string) {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ group_name: groupName }),
    });
    return handleResponse(response);
  },

  async getGroup(groupId: string) {
    const response = await fetchWithAuth(`${API_BASE_URL}/groups/${groupId}`);
    return handleResponse(response);
  },
};

// Transaction API
export const transactionApi = {
  async createTransaction(transactionData: {
    date: string;
    description: string;
    category: string;
    amount: number;
    payer_id: string;
    group_id: string;
  }): Promise<ApiTransaction> {
    const response = await fetchWithAuth(`${API_BASE_URL}/transactions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(transactionData),
    });
    return handleResponse(response);
  },

  async getMonthlyTransactions(
    groupId: string,
    month?: string,
    page?: number,
    limit?: number
  ): Promise<ApiMonthlyData> {
    const params = new URLSearchParams();
    if (month) params.append("month", month);
    if (page) params.append("page", page.toString());
    if (limit) params.append("limit", limit.toString());

    const url = `${API_BASE_URL}/groups/${groupId}/transactions${
      params.toString() ? "?" + params.toString() : ""
    }`;

    const response = await fetchWithAuth(url);
    return handleResponse(response);
  },

  async getMonthlySummary(
    groupId: string,
    month?: string
  ): Promise<ApiMonthlySummary> {
    const params = new URLSearchParams();
    if (month) params.append("month", month);

    const url = `${API_BASE_URL}/groups/${groupId}/transactions/summary${
      params.toString() ? "?" + params.toString() : ""
    }`;

    const response = await fetchWithAuth(url);
    return handleResponse(response);
  },

  async getYearlySummary(groupId: string, year?: number) {
    const params = new URLSearchParams();
    if (year) params.append("year", year.toString());

    const url = `${API_BASE_URL}/groups/${groupId}/transactions/yearly-summary${
      params.toString() ? "?" + params.toString() : ""
    }`;

    const response = await fetchWithAuth(url);
    return handleResponse(response);
  },

  async updateTransaction(
    transactionId: string,
    updates: Partial<{
      date: string;
      description: string;
      category: string;
      amount: number;
      payer_id: string;
    }>
  ): Promise<ApiTransaction> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/transactions/${transactionId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updates),
      }
    );
    return handleResponse(response);
  },

  async deleteTransaction(transactionId: string) {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/transactions/${transactionId}`,
      {
        method: "DELETE",
      }
    );
    return handleResponse(response);
  },
};
